import { useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertTriangle, Coins, Loader2, RefreshCw } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchDeviceCreditHistory } from "@/hooks/use-device-credit-history";
import { useMainOrgCameras, useSubOrgDevices } from "@/hooks/use-sub-organizations";

interface SubOrganizationCreditsProps {
  subOrganizationId: string;
  parentOrganizationId: string;
}

export const SubOrganizationCredits = ({
  subOrganizationId,
  parentOrganizationId,
}: SubOrganizationCreditsProps) => {
  const [selectedCamera, setSelectedCamera] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const { data: allocations = [], isLoading: allocationsLoading } = useSubOrgDevices(subOrganizationId);
  const { data: parentCameras = [], isLoading: camerasLoading } = useMainOrgCameras(parentOrganizationId);
  const deviceIds = useMemo(() => allocations.map((allocation) => allocation.device_id), [allocations]);
  const deviceIdKey = deviceIds.join(",");

  const historyQueries = useQueries({
    queries: deviceIds.map((deviceId) => ({
      queryKey: ["device-credit-history", deviceId],
      queryFn: () => fetchDeviceCreditHistory(deviceId),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const cameraRows = useMemo(() => {
    return deviceIds.map((deviceId, index) => {
      const camera = parentCameras.find((item) => item.device_id === deviceId);
      return {
        deviceId,
        name: camera?.device_name || deviceId,
        serial: camera?.device_serial || null,
        query: historyQueries[index],
      };
    });
  }, [deviceIdKey, parentCameras, historyQueries]);

  const months = useMemo(() => {
    const values = new Set<string>();
    cameraRows.forEach((camera) => camera.query.data?.months.forEach((month) => values.add(month)));
    return Array.from(values).sort((a, b) => b.localeCompare(a));
  }, [cameraRows]);

  const scopedRows = selectedCamera === "all"
    ? cameraRows
    : cameraRows.filter((camera) => camera.deviceId === selectedCamera);

  const eventsFor = (camera: (typeof cameraRows)[number]) => {
    const events = camera.query.data?.events ?? [];
    return selectedPeriod === "all" ? events : events.filter((event) => event.month === selectedPeriod);
  };

  const totalCredits = scopedRows.reduce((total, camera) => total + eventsFor(camera).length, 0);
  const chartData = useMemo(() => {
    const byDay = new Map<string, number>();
    scopedRows.forEach((camera) => {
      eventsFor(camera).forEach((event) => {
        const key = format(event.timestamp, "yyyy-MM-dd");
        byDay.set(key, (byDay.get(key) ?? 0) + 1);
      });
    });
    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, credits]) => ({
        date,
        day: format(new Date(`${date}T00:00:00`), selectedPeriod === "all" ? "MMM dd, yyyy" : "MMM dd"),
        credits,
      }));
  }, [scopedRows, selectedPeriod]);

  const isLoading = allocationsLoading || camerasLoading || historyQueries.some((query) => query.isLoading);
  const failedCount = historyQueries.filter((query) => query.isError).length;
  const succeededCount = historyQueries.filter((query) => query.isSuccess).length;
  const selectedCameraName = selectedCamera === "all"
    ? "All cameras"
    : cameraRows.find((camera) => camera.deviceId === selectedCamera)?.name || "Selected camera";

  const refresh = () => historyQueries.forEach((query) => query.refetch());

  if (allocationsLoading || camerasLoading) {
    return (
      <div className="flex min-h-48 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (deviceIds.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Credits</CardTitle>
          <CardDescription>No cameras are shared with this sub-customer yet.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-muted p-2">
                <Coins className="h-5 w-5" />
              </div>
              <div>
                <CardTitle>Used image credits</CardTitle>
                <CardDescription>Image credits used by cameras shared with this sub-customer.</CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={refresh} disabled={historyQueries.some((query) => query.isFetching)}>
              <RefreshCw className={`h-4 w-4 mr-1 ${historyQueries.some((query) => query.isFetching) ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="space-y-1.5 sm:min-w-64">
              <span className="text-sm font-medium">Camera</span>
              <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cameras</SelectItem>
                  {cameraRows.map((camera) => (
                    <SelectItem key={camera.deviceId} value={camera.deviceId}>
                      {camera.name}{camera.serial ? ` · ${camera.serial}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:w-48">
              <span className="text-sm font-medium">Period</span>
              <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  {months.map((month) => <SelectItem key={month} value={month}>{month}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {failedCount > 0 && succeededCount > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Credit history could not be loaded for {failedCount} camera{failedCount === 1 ? "" : "s"}. The totals below include the available cameras only.
              </AlertDescription>
            </Alert>
          )}

          {failedCount === historyQueries.length && !isLoading ? (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Credit history could not be loaded. Please try again.</AlertDescription>
            </Alert>
          ) : isLoading && succeededCount === 0 ? (
            <div className="flex h-64 items-center justify-center text-sm text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading credit history…
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-end justify-between gap-3 border-y py-4">
                <div>
                  <p className="text-sm text-muted-foreground">{selectedCameraName} · {selectedPeriod === "all" ? "All time" : selectedPeriod}</p>
                  <p className="text-3xl font-semibold text-foreground">{totalCredits.toLocaleString()}</p>
                  <p className="text-sm text-muted-foreground">used image credits</p>
                </div>
                <Badge variant="outline">{scopedRows.length} camera{scopedRows.length === 1 ? "" : "s"}</Badge>
              </div>

              {chartData.length === 0 ? (
                <p className="py-16 text-center text-sm text-muted-foreground">No credit history recorded for this selection.</p>
              ) : (
                <div className="h-[300px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} minTickGap={24} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => [value, "Credits"]} labelFormatter={(label) => String(label)} />
                      <Bar dataKey="credits" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} name="Credits" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Credits by camera</CardTitle>
          <CardDescription>Select a camera to drill into its history.</CardDescription>
        </CardHeader>
        <CardContent className="divide-y p-0">
          {cameraRows.map((camera) => {
            const count = eventsFor(camera).length;
            return (
              <Button
                key={camera.deviceId}
                type="button"
                variant="ghost"
                className="h-auto w-full justify-between rounded-none px-6 py-3 text-left"
                onClick={() => setSelectedCamera(camera.deviceId)}
                disabled={camera.query.isLoading || camera.query.isError}
              >
                <span className="min-w-0">
                  <span className="block truncate font-medium">{camera.name}</span>
                  <span className="block truncate text-sm text-muted-foreground">{camera.serial || camera.deviceId}</span>
                </span>
                {camera.query.isLoading ? (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                ) : camera.query.isError ? (
                  <Badge variant="destructive">Unavailable</Badge>
                ) : (
                  <span className="shrink-0 text-right">
                    <span className="block font-semibold">{count.toLocaleString()}</span>
                    <span className="block text-xs text-muted-foreground">credits</span>
                  </span>
                )}
              </Button>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubOrganizationCredits;